import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { EMPTY, EmptyError, Observable, of, throwError } from 'rxjs';
import { Router, ActivationEnd } from '@angular/router';
import { catchError, takeUntil, mergeMap, delay, retryWhen, tap, map } from 'rxjs/operators';
import { HttpCancelService, LocalService } from '@app/services';
import { Alert } from '@app/models';
import { environment } from '@environment/environment';

import Swal from 'sweetalert2';
import * as _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';

export const maxRetries = 1;
export const delayMs = 2000;

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    toast = new Alert();

    public message: string;

    // Set pour suivre les tokens des requêtes actives
    private activeRequestTokens = new Set<string>();


    constructor(
        public translate: TranslateService,
        private localService: LocalService,
        router: Router,
        private httpCancelService: HttpCancelService
    ) {
        router.events.subscribe(
            event => {
                // An event triggered at the end of the activation part of the Resolve phase of routing.
                if (event instanceof ActivationEnd) {
                    // Cancel all pending calls
                    this.activeRequestTokens.forEach(token => {
                        this.httpCancelService.cancelPendingRequests(token);
                    });
                    this.activeRequestTokens.clear();
                }
            }
        ); 
    }

    // add authorization header with basic auth credentials if available
    private addToken(request: HttpRequest<any>, option: string, value: string) {
        return request.clone({
            setHeaders: {
                [option]: value
            },
            responseType: 'json'
        });
    }

    private addLanguageHeaders(request: HttpRequest<any>) {
        const language = this.getCurrentLanguage();

        return request.clone({
            setHeaders: {
                'Accept-Language': language,
                'X-App-Language': language
            },
            responseType: 'json'
        });
    }

    /**
     * Les appels de l'API envoient des objets JSON. Plusieurs services les
     * sérialisent déjà avec JSON.stringify(), ce qui laisse Angular choisir
     * text/plain par défaut. OWASP CRS refuse ce type pour les POST API.
     */
    private addJsonContentType(request: HttpRequest<any>) {
        const hasBody = request.body !== null && request.body !== undefined;
        const isWriteRequest = ['POST', 'PUT', 'PATCH'].includes(request.method);
        const isApiRequest = request.url.startsWith(environment.apiEndPoint);
        const isFormData = typeof FormData !== 'undefined' && request.body instanceof FormData;

        if (!hasBody || !isWriteRequest || !isApiRequest || isFormData || request.headers.has('Content-Type')) {
            return request;
        }

        return request.clone({
            setHeaders: {
                'Content-Type': 'application/json'
            }
        });
    }

    private getCurrentLanguage(): string {
        return this.localService.getJsonValue('currentLangue') || this.translate.currentLang || this.translate.defaultLang || 'en-US';
    }

    private translateBackendMessage(message: any): any {
        if (typeof message !== 'string' || !message.trim()) {
            return message;
        }

        return this.translate.instant(message);
    }

    private translateHttpError(error: any): any {
        if (error?.error?.message) {
            error.error.message = this.translateBackendMessage(error.error.message);
        }

        return error;
    }

    // Interception requete http vers Serveur
    intercept<T>(request: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
        const cancelToken = request.url;

        request = this.addLanguageHeaders(request);
        request = this.addJsonContentType(request);

        // Récupération du JWT dans le stockage local
        const jwt = this.localService.getJsonValue('token');
        const token = typeof jwt === 'string' ? jwt.trim() : '';

        // Si le JWT existe, on l'ajoute au header de la requête
        if (token) {
            const tokenValue = /^(?:JWT|Bearer)\s+/i.test(token) ? token : `JWT ${token}`;
            request = this.addToken(request,'Authorization', tokenValue);
        }

        //renvoi de la reponse vers le serveur
        return next.handle(request).pipe(
            takeUntil(this.httpCancelService.onCancelPendingRequests(cancelToken)),
            map(event => {
                if (event instanceof HttpResponse && event.body?.message) {
                    return event.clone({
                        body: {
                            ...event.body,
                            message: this.translateBackendMessage(event.body.message)
                        }
                    });
                }

                return event;
            }),
            tap(() => this.activeRequestTokens.delete(cancelToken)),
            retryWhen((error) => {
                return error.pipe(
                    mergeMap((error, index) => {
                        error = this.translateHttpError(error);

                        // Si l'erreur est 502 et que nous sommes en dessous du nombre maximal de tentatives, réessayez
                        if (error.status == 502 && index < maxRetries) {
                            return of(error).pipe(delay(delayMs));
                        }

                        // Si nous avons atteint le nombre maximal de tentatives ou si l'erreur n'est pas 502, affichez une alerte et renvoyez une erreur
                        if (error.status == 502) {
                            Swal.fire({ ...this.toast, icon: 'error', title: this.translate.instant('Error'), html: error?.error?.message });
                        }

                        // Renvoyez une erreur pour les autres scénarios
                        return throwError(error);
                        //return throwError(this.translate.instant('An error occurred while fetching data. Please try again later.'));
                    })
                )
            }),
            catchError(err => {
                if (err instanceof EmptyError) {
                    // Gérer l'erreur ici
                    return EMPTY; // Retourner un observable vide
                }
                throw this.translateHttpError(err); // Rethrow pour les autres erreurs
            })
        );
    }
}
